<script setup lang="ts">
import type {
    EOrderLineType,
    EPaymentTarget,
    EPeriodicity,
    HeroComponentModel,
    InterventionAside,
    InterventionDetailComponentModel,
    InterventionDetailContentItem,
    InterventionGetPriceRequestModel,
    InterventionMedia,
} from '@su4r-frontend/api';
import type { CartOrderModel } from '@/types/product';

const props = defineProps<{
    data: InterventionDetailContentItem;
}>();

const { id, name, type } = props.data;

const { data: authData, status } = useAuth();
const { locale } = useI18n();

// Adds cookie to request for server server side rendering
const headers = useRequestHeaders(['cookie']);

const { data: pricesData } = await useAsyncData(`price-${id}`, async () => {
    return $fetch('/api/interventions/prices', {
        method: 'POST',
        headers,
        body: {
            interventionIds: [id],
            culture: locale.value,
        } as InterventionGetPriceRequestModel,
    });
});

const localePath = useLocalePath();
const translatedRoutes = useTranslatedRoutes();

const { alertSettings, hideAlert, showAlert } = useAlertToast();
const { goToPrescribeFlow } = usePrescribeIntervention();

const orderDTO = reactive<CartOrderModel>({
    culture: locale.value,
    practitioner: {
        emailAddress: authData.value?.user?.email ?? '',
        id: authData.value?.user?.name ?? '',
    },
    orderLines: [{ productReference: id, name, type: type as EOrderLineType }],
    prescription: {
        supplierInstructions: [],
        duration: {
            amount: 1,
            periodicity: 'Monthly' as EPeriodicity,
        },
        instructions: '',
    },
    paymentTargetType: 'Clinic' as EPaymentTarget,
});

const heroData = props.data.components.find(
    (comp): comp is HeroComponentModel => comp.alias === 'heroComponent'
);

const interventionData = props.data.components.find(
    (comp): comp is InterventionDetailComponentModel => comp.alias === 'interventionDetailComponent'
);

const asideData = computed<InterventionAside | null>(() => {
    return interventionData?.aside ?? null;
});

const carouselData = computed<InterventionMedia | null>(() => {
    return interventionData?.media ?? null;
});

const interventionComponents = computed(() => {
    return interventionData?.interventionContentComponents ?? null;
});
</script>

<template>
    <div class="container grid">
        <PageHero v-bind="heroData" />
        <div class="grid grid-cols-12">
            <div class="col-span-12 lg:col-span-8">
                <ProductCarousel
                    v-if="carouselData && carouselData?.items.length > 0"
                    :data="carouselData" />
            </div>
            <div class="col-span-12 flex flex-col gap-4 lg:col-span-4 lg:row-span-2 lg:gap-6">
                <AboutApp
                    v-if="asideData"
                    :data="asideData.features"
                    :price="pricesData ? pricesData.prices[id] : props.data.price">
                    <button
                        v-if="status === 'authenticated' && authData?.user.role === 'practitioner'"
                        type="button"
                        class="btn-primary-b"
                        @click="
                            goToPrescribeFlow(orderDTO, {
                                onError: () =>
                                    showAlert(
                                        `Product couldn't be added to the prescription`,
                                        'error',
                                        ANIMATION_DURATION
                                    ),
                            })
                        ">
                        {{ $t('interventionDetail.prescribeIntervention') }}
                    </button>
                    <NuxtLink
                        v-else
                        class="btn-primary-b"
                        :to="localePath(translatedRoutes.register)">
                        {{ $t('interventionDetail.addToCartButtonTextGeneral') }}
                    </NuxtLink>
                </AboutApp>
                <AboutDev v-if="asideData?.vendorInfo" :data="asideData?.vendorInfo" />
            </div>
            <div class="col-span-12 grid lg:col-span-8">
                <template
                    v-for="(component, index) in interventionComponents"
                    :key="`${component.alias}-${index}`">
                    <component :is="mapComponentAlias(component.alias ?? '')" v-bind="component" />
                </template>
            </div>
        </div>
    </div>
    <ClientOnly>
        <Teleport to="body">
            <AlertToast :alert-settings="alertSettings" @hide-alert="hideAlert" />
        </Teleport>
    </ClientOnly>
</template>
